import React, { useEffect, useMemo, useState } from 'react';
import { Car, AlertTriangle, Home } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';

const cache = new Map();
const keyOf = (a, b) => `${a}|${b}`;
const finishPc = (j) => j.to_postcode || j.from_postcode || '';
const startPc = (j) => j.from_postcode || j.to_postcode || '';
const fmtMin = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`);

const buildLegs = (jobs, startOf, durOf, homePc) => {
  const sorted = [...jobs].sort((a, b) => startOf(a) - startOf(b));
  const legs = sorted.slice(0, -1).map((a, i) => {
    const b = sorted[i + 1];
    return { id: `${a.id}-${b.id}`, ref: b.ref, from: finishPc(a), to: startPc(b), endMin: startOf(a) + durOf(a), nextStart: startOf(b) };
  });
  const last = sorted[sorted.length - 1];
  if (last && homePc) legs.push({ id: `${last.id}-home`, ref: `home-${last.ref}`, from: finishPc(last), to: homePc, endMin: startOf(last) + durOf(last), nextStart: null, home: true });
  return legs.filter(l => l.from && l.to);
};

const listeners = new Set();
const notify = () => listeners.forEach(fn => fn(n => n + 1));

const useRoutes = (legs) => {
  const { authFetch } = useAuth();
  const [, bump] = useState(0);
  const sig = legs.map(l => keyOf(l.from, l.to)).join(',');
  useEffect(() => { listeners.add(bump); return () => { listeners.delete(bump); }; }, []);
  useEffect(() => {
    const missing = legs.filter(l => !cache.has(keyOf(l.from, l.to)));
    if (!missing.length) return;
    missing.forEach(l => cache.set(keyOf(l.from, l.to), null));
    authFetch('post', '/travel/legs', { pairs: missing.map(l => ({ from_pc: l.from, to_pc: l.to })) })
      .then(({ data }) => data.legs.forEach(r => cache.set(keyOf(r.from_pc, r.to_pc), r.route || false)))
      .catch(() => missing.forEach(l => cache.delete(keyOf(l.from, l.to))))
      .finally(notify);
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return (l) => cache.get(keyOf(l.from, l.to));
};

const Pill = ({ leg, route, top, compact }) => {
  const late = route && leg.nextStart !== null && leg.endMin + route.minutes > leg.nextStart;
  const short = late ? leg.endMin + route.minutes - leg.nextStart : 0;
  const tip = route ? `${leg.home ? 'Back to London · ' : ''}${leg.from} → ${leg.to}: ${route.miles} miles, ${fmtMin(route.minutes)} drive${route.estimated ? ' (estimate)' : ''}${late ? ` — ${fmtMin(short)} late for next job` : ''}` : `${leg.from} → ${leg.to}`;
  return (
    <div className={`absolute left-1 right-1 z-20 pointer-events-auto rounded-full border text-[10px] font-black leading-none flex items-center justify-center gap-1 px-1.5 h-5 shadow-sm truncate ${late ? 'bg-red-600 border-red-700 text-white' : leg.home ? 'bg-sky-50 border-sky-300 text-sky-900' : 'bg-white border-slate-300 text-[color:var(--mv-navy)]'}`}
      style={{ top }} title={tip} data-testid={`travel-leg-${leg.ref}`} data-late={late ? 'true' : 'false'}>
      {late ? <AlertTriangle className="w-3 h-3 shrink-0" /> : leg.home ? <Home className="w-3 h-3 shrink-0" /> : <Car className="w-3 h-3 shrink-0" />}
      {route === undefined || route === null ? <span className="opacity-60">…</span> : route === false ? <span className="opacity-60">?</span> : compact
        ? <span className="truncate">{Math.round(route.miles)}mi·{route.minutes}m</span>
        : <span className="truncate">{leg.home ? 'Back to London · ' : ''}{route.miles} mi · {fmtMin(route.minutes)}{late ? ` · ${fmtMin(short)} late` : ''}</span>}
    </div>
  );
};

export const TravelLegs = ({ jobs, startOf, durOf, toPx, homePc, compact = false }) => {
  const legs = useMemo(() => buildLegs(jobs, startOf, durOf, homePc), [jobs, startOf, durOf, homePc]);
  const routeOf = useRoutes(legs);
  return legs.map(l => {
    const a = toPx(l.endMin);
    const gap = toPx(l.nextStart) - a;
    return <Pill key={l.id} leg={l} route={routeOf(l)} compact={compact} top={l.home ? a + 3 : gap > 22 ? a + (gap - 20) / 2 : a - 10} />;
  });
};

export const useDayMiles = (start, days, refreshKey) => {
  const { authFetch } = useAuth();
  const [res, setRes] = useState({ totals: {}, yard: null });
  useEffect(() => {
    let live = true;
    authFetch('get', `/travel/day-miles?start=${start}&days=${days}`).then(({ data }) => { if (live) setRes(data); }).catch(() => {});
    return () => { live = false; };
  }, [authFetch, start, days, refreshKey]);
  return res;
};

export const DayMiles = ({ t, testId, compact = false }) => {
  if (!t || !t.miles) return null;
  const p = t.parts;
  const f = t.fuel;
  const fuelTip = f ? ` · Fuel ≈ £${f.cost.toFixed(2)} (${f.litres} L at ${f.mpg} mpg${f.van ? `, ${f.van}` : ''}${f.default_used ? ', default mpg' : ''}, UK average diesel price)` : '';
  const tip = `Day total ${t.miles} miles, about ${fmtMin(t.minutes)} driving — yard out ${p.yard_out} mi, jobs ${p.jobs} mi, between jobs ${p.between} mi, back to yard ${p.yard_back} mi${t.unknown ? ` (${t.unknown} leg(s) missing a postcode)` : ''}${fuelTip}`;
  return (
    <span className="inline-flex flex-wrap items-center gap-0.5 max-w-full text-[10px] font-black leading-tight" title={tip} data-testid={testId}>
      <span className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-[color:var(--mv-navy)] text-white px-1.5 py-0.5"><Car className="w-3 h-3" />{compact ? `${Math.round(t.miles)}mi` : `${t.miles} mi · ${fmtMin(t.minutes)}`}</span>
      {f && <span className="whitespace-nowrap rounded-full bg-[color:var(--mv-orange)] text-white px-1.5 py-0.5" data-testid={`${testId}-fuel`}>≈ £{f.cost.toFixed(2)}</span>}
    </span>
  );
};

export const addrLine = (b, side) => [[b[`${side}_house_number`], b[`${side}_address`]].filter(Boolean).join(' '), b[`${side}_postcode`]].filter(Boolean).join(', ');
